import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IngestFile, IngestionService } from '../../../../../js/api';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { Heading } from '../../../components/heading';
import { ListFilter } from '../../../components/list-filter';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { TooltippedText } from '../../../components/tooltipped-text';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { formatTimestamp } from '../../../utils/date';
import { getIngestFileStatusBadgeColor, STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { IngestUploadDialog } from './IngestUploadDialog';
import { STAFF_INGEST_FILES_QUERY_KEY } from './queryKeys';

const PAGE_SIZE = 25;

// A file stuck in Uploaded that never produced rows (e.g. processing was
// never picked up) can simply be deleted — anything processed is audit data.
const canDelete = (file: IngestFile): boolean =>
  file.current_status.code === StatusCodes.UPLOADED && (file.rows_total ?? 0) === 0;

export const StaffIngestFilesList: React.FC = () => {
  useDocumentTitle('Imports');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IngestFile | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => IngestionService.ingestionFilesDestroy({ id }),
    onSuccess: () => {
      toast.success('File deleted');
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: [STAFF_INGEST_FILES_QUERY_KEY] });
    },
    onError: (error: Error) => handleApiError(error, { messagePrefix: 'Delete failed' }),
  });

  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    displayTypePrefKey: 'StaffIngestFiles',
  });

  const statuses = useMemo(() => filterValues.statuses || [], [filterValues.statuses]);
  const healthcareEntity = filterValues.healthcare_entity || '';
  const ordering = (filterValues.ordering || '-created_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({ ...filterValues, ordering: next });
    },
    [updateFilters, filterValues]
  );

  const queryKey = useMemo(
    () => [STAFF_INGEST_FILES_QUERY_KEY, statuses, healthcareEntity, ordering],
    [statuses, healthcareEntity, ordering]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey,
      queryFn: async ({ pageParam = 0 }) =>
        IngestionService.ingestionFilesList({
          currentStatusIn: statuses.length > 0 ? statuses : undefined,
          healthcareEntity: healthcareEntity || undefined,
          ordering,
          limit: PAGE_SIZE,
          offset: pageParam as number,
        }),
      getNextPageParam: (lastPage, allPages) =>
        lastPage.next ? allPages.length * PAGE_SIZE : undefined,
    });

  const files = useMemo(() => data?.pages.flatMap((page) => page.results || []) || [], [data]);
  const totalCount = data?.pages[0]?.count || 0;

  const loadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMore, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  const { data: reportTypeMap = {} } = useCodeDefinitionsAsMap('IngestReportType');

  return (
    <main className={MAIN_CONTENT_AREA} data-testid="ingest-files-list-page">
      <div className={STICKY_HEADER}>
        <div className={PAGE_HEADER}>
          <Heading level={1} className="tw:!text-brand-green-0">
            <i className="far fa-fw fa-file-import tw:mr-1" />
            Imports
          </Heading>
          <Button
            color="brandgreen1"
            onClick={() => setShowUploadDialog(true)}
            data-testid="ingest-upload-button"
          >
            Upload Report
          </Button>
          <IngestUploadDialog
            open={showUploadDialog}
            onClose={() => setShowUploadDialog(false)}
            onSuccess={() =>
              queryClient.invalidateQueries({ queryKey: [STAFF_INGEST_FILES_QUERY_KEY] })
            }
          />
        </div>

        {isError && (
          <div className="tw:mt-4 tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700">
            <p className="tw:font-semibold">Something went wrong</p>
            <p className="tw:text-sm">
              Please try again later. If the problem persists, please contact us.
            </p>
          </div>
        )}

        <div className="tw:mt-1">
          <ListFilter
            show_search={false}
            workflow_scope={WorkflowScopes.IngestFile}
            showStatus={true}
            showReason={false}
            showOrganizationFilter={true}
            showDisplayType={false}
            sortOptions={[
              { label: 'Uploaded', value: 'created_at' },
              { label: 'Processed', value: 'processed_at' },
              { label: 'Open Errors', value: 'rows_error' },
            ]}
            values={filterValues}
            onFilterChange={updateFilters}
          />
        </div>
      </div>

      <div className="tw:mt-3">
        {isLoading && files.length === 0 && (
          <div className="tw:text-center tw:py-8">Loading files...</div>
        )}
        {!isLoading && files.length === 0 ? (
          <div data-testid="ingest-files-empty" className="tw:text-center tw:text-zinc-500 tw:py-8">
            No import files found.
            <div className="tw:text-zinc-500 tw:italic tw:text-sm">
              Upload a report file to get started.
            </div>
          </div>
        ) : (
          <>
            {files.length > 0 && (
              <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                Showing {files.length} of {totalCount} files
              </div>
            )}
            <Table striped dense>
              <TableHead>
                <TableHeader>Client</TableHeader>
                <TableHeader>Report</TableHeader>
                <TableHeader>File</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Rows</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Updated</TableHeader>
                <TableSortHeader
                  sortKey="rows_error"
                  ordering={ordering}
                  onOrderingChange={setOrdering}
                >
                  Open Errors
                </TableSortHeader>
                <TableSortHeader
                  sortKey="created_at"
                  ordering={ordering}
                  onOrderingChange={setOrdering}
                >
                  Uploaded
                </TableSortHeader>
                <TableHeader>
                  <span className="tw:sr-only">Actions</span>
                </TableHeader>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow
                    key={file.id}
                    data-testid={`ingest-file-row-${file.id}`}
                    href={`/staff/ingest/${file.id}`}
                  >
                    <TableCell>{file.healthcare_entity_name}</TableCell>
                    <TableCell>{reportTypeMap[file.report_type] || file.report_type}</TableCell>
                    <TableCell className="tw:max-w-[220px] tw:truncate">
                      {file.original_filename}
                    </TableCell>
                    <TableCell>
                      <Badge color={getIngestFileStatusBadgeColor(file.current_status.code)}>
                        {file.current_status.title}
                      </Badge>
                    </TableCell>
                    <TableCell>{file.rows_total}</TableCell>
                    <TableCell>{file.rows_created}</TableCell>
                    <TableCell>{file.rows_updated}</TableCell>
                    <TableCell>
                      {file.rows_error > 0 ? (
                        <Badge color="red">{file.rows_error}</Badge>
                      ) : (
                        <span className="tw:text-zinc-500">0</span>
                      )}
                    </TableCell>
                    <TableCell>{formatTimestamp(file.created_at)}</TableCell>
                    <TableCell>
                      {canDelete(file) && (
                        <TooltippedText tooltipContent="Delete">
                          <Button
                            small
                            color="stoplight-danger-outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(file);
                            }}
                            title="Delete"
                            data-testid={`ingest-file-delete-${file.id}`}
                          >
                            <i className="far fa-fw fa-trash-can" />
                          </Button>
                        </TooltippedText>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {hasNextPage && (
              <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                {isFetchingNextPage && (
                  <div className="tw:text-center tw:text-zinc-500">Loading more files...</div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete File</DialogTitle>
        <DialogBody>
          <p className="tw:text-zinc-700">
            Delete <span className="tw:font-semibold">{deleteTarget?.original_filename}</span>? This
            file was uploaded but never processed any rows, so nothing else will be affected.
          </p>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="stoplight-danger"
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            disabled={deleteMutation.isPending}
            data-testid="ingest-file-delete-confirm"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
};
